import type {
    DeviceByTicketTemplateResponse,
    DeviceMaintenanceHistoriesResponse,
    DevicePayload,
    DeviceResponse,
    HistoryUpdateAttributeDeviceResponse,
} from './../../types/apps/device.type';

import type {
    ApiResponse,
    PaginatedResult,
    QueryParams,
} from '@/types/common/api.type';
import type { BaseApiClientInterface } from '@/types/service/base-api-client.interface';
import type { Creatable } from '@/types/service/creatable.interface';
import type { Deletable } from '@/types/service/deletable.interface';
import type { Indexable } from '@/types/service/indexable.interface';
import type { Showable } from '@/types/service/showable.interface';
import type { Updatable } from '@/types/service/updatable.interface';

export interface DeviceServiceInterface {}

type DeviceListResponse = ApiResponse<PaginatedResult<DeviceResponse>>;
type DeviceDetailResponse = ApiResponse<DeviceResponse>;
type HistoryUpdateAttributeDevice =
    ApiResponse<HistoryUpdateAttributeDeviceResponse>;

type DeviceByTicketTemplate = ApiResponse<DeviceByTicketTemplateResponse>;
export class DeviceService
    implements Showable, Updatable, Deletable, Creatable, Indexable
{
    public constructor(private apiClient: BaseApiClientInterface) {}

    public endpoints = {
        base: () => '/api/devices',
        createWithDetail: () => '/api/devices/with-detail',
        detail: (id: string) => `/api/devices/${id}`,
        history: () => `/api/devices/History`,
        import: () => '/api/devices/import',
        export: () => '/api/devices/export',
        historyUpdateAttribute: () => '/api/devices/history-update-dattribute',
        getByTicketTemplate: () => '/api/devices/get-by-ticket-template',
        checkTaskRepeat: (id: string) => `/api/devices/check-task-repeat/${id}`,
    };

    public index(params?: QueryParams): Promise<DeviceListResponse> {
        return this.apiClient
            .fetchApi<
                PaginatedResult<DeviceResponse>
            >(this.endpoints.base(), params)
            .then(res => {
                return {
                    ...res,
                    result: {
                        ...res.result,
                        items: res.result.items.map(item => ({
                            ...item,
                            customName: `${item.serialNumber} - ${item.name}`,
                        })),
                    },
                };
            });
    }

    public detail(
        id: string,
        params?: QueryParams
    ): Promise<DeviceDetailResponse> {
        return this.apiClient.fetchApi<DeviceResponse>(
            this.endpoints.detail(id),
            params
        );
    }

    public create(
        payload: DevicePayload
    ): Promise<ApiResponse<DeviceResponse>> {
        return this.apiClient.postApi(
            this.endpoints.createWithDetail(),
            payload
        );
    }

    public update(
        id: string,
        payload: DevicePayload
    ): Promise<ApiResponse<DeviceResponse>> {
        return this.apiClient.putApi(this.endpoints.detail(id), payload);
    }

    public destroy(ids: string[], isHardDeleted?: boolean): Promise<any> {
        return this.apiClient.destroyApi<void>(
            this.endpoints.base(),
            ids,
            isHardDeleted
        );
    }

    public getHistory(
        params?: QueryParams
    ): Promise<
        ApiResponse<PaginatedResult<DeviceMaintenanceHistoriesResponse>>
    > {
        return this.apiClient.fetchApi(this.endpoints.history(), params);
    }

    public async importFile(file: File): Promise<any> {
        const form = this.apiClient.buildFormData({ file });

        return this.apiClient.postApi<any>(this.endpoints.import(), form);
    }

    public async exportFile(): Promise<Blob> {
        return this.apiClient.fetchBlob(this.endpoints.export());
    }

    public getHistoryOfUpdateAttribute(
        params?: QueryParams
    ): Promise<HistoryUpdateAttributeDevice> {
        return this.apiClient.fetchApi(
            this.endpoints.historyUpdateAttribute(),
            params
        );
    }

    public getByTicketTemplate(
        params?: QueryParams
    ): Promise<DeviceByTicketTemplate> {
        return this.apiClient.fetchApi(
            this.endpoints.getByTicketTemplate(),
            params
        );
    }

    public checkTaskRepeat(params?: {
        deviceId: string;
        ticketId?: string;
    }): Promise<DeviceDetailResponse> {
        return this.apiClient.fetchApi<DeviceResponse>(
            this.endpoints.checkTaskRepeat(params?.deviceId || ''),
            params
        );
    }
}
